import { notify } from './notify';
import { showErrorModal } from './errorModalEmitter';

/**
 * Global error handler for API errors across all call patterns:
 * - Factory hooks (createEntityQuery/createEntityMutation) via globalErrorHandler
 * - notify.promise() catch block
 * - ConfirmDialog onAction catch via confirmErrorHandler
 * - safeApiCall() wrapper
 *
 * Handles:
 * - Validation errors (400 with validationErrors array)
 * - Business errors (400/500 with error.message)
 * - Server errors (500+ with no structured body → error modal)
 * - Permission denied (403 from mutations → generic toast)
 * - Suppressed messages (OTP, template, file upload errors)
 *
 * Silently ignores:
 * - 401 (auth interceptor handles token refresh)
 * - 403 from GET requests (interceptor fires PERMISSION_CHANGED → page redirect)
 *
 * Shared across all apps (customer, participant, etc.)
 */
export function showApiErrorToast(error: unknown): void {
  // Normalize error shape: OpenAPI ApiError vs Axios error
  const raw = error as Record<string, any>;

  // OpenAPI ApiError: { status, body: { error: { message, validationErrors } } }
  // Axios error: { response: { status, data: { error: { message, validationErrors } } }, config: { method } }
  const status: number | undefined = raw?.status ?? raw?.response?.status;
  const errorPayload: { message?: string; validationErrors?: Array<{ message: string }> } | undefined =
    raw?.body?.error ?? raw?.response?.data?.error;

  if (status === 401) return;

  // 403 handling: GET-403s are session-level (interceptor fires PERMISSION_CHANGED).
  // Mutation-403s (POST/PUT/DELETE) are action-level — show a toast.
  if (status === 403) {
    const method = (raw?.config?.method ?? raw?.request?.method ?? '').toLowerCase();
    const isGetRequest = method === '' || method === 'get';
    if (isGetRequest) return;
    const msg = errorPayload?.message;
    notify.error(msg && msg.length > 0 ? msg : 'You do not have permission to perform this action.', { autoClose: 30000 });
    return;
  }

  const suppressedPatterns = [
    'OTP is invalid',
    'Invalid Template',
    'Only excel file can be uploaded',
    'Index was outside the bounds of the array.',
  ];

  const isSuppressed = (msg: string) =>
    suppressedPatterns.some((pattern) => msg.includes(pattern));

  if (errorPayload?.validationErrors != null) {
    const messages = errorPayload.validationErrors.map((e) => e.message);
    if (!messages.some(isSuppressed)) {
      notify.error(messages.join(', '), { toastId: 'api-validation-error', autoClose: 30000 });
    }
  } else if (errorPayload?.message && errorPayload.message.length > 0) {
    if (!isSuppressed(errorPayload.message)) {
      notify.error(errorPayload.message, { autoClose: 30000 });
    }
  } else if (status != null && status >= 500) {
    showErrorModal({ status });
  }
}
